/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Controller } from 'react-hook-form';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import Iconify from './Iconify';
import AssignedToLead from './AssignedToLead';
import { btnHandler } from '../utils/reminderSelectedTimeFunc';
import { useSelector } from '../redux/store';
import { STEP_BADGE, SECTION_CARD, SECTION_HEADER_LABEL, SECTION_ICON_COLOR, compactChipStyle } from '../styles/stepFormStyles';

const REMINDER_PRESETS = [
    { key: '2 hours-call', label: '2 hours' },
    { key: 'tomorrow', label: 'Tomorrow 9am' },
    { key: 'week', label: '1 week' },
    { key: 'two-week', label: '2 weeks' },
    { key: 'custom reminder', label: 'Custom' },
    { key: 'no-reminder', label: 'No reminder' },
];

/**
 * Shared reminder preset selector used across:
 * - WhatsApp Follow Up step
 * - Spoke - Not Booked (AppointmentNotBooked)
 * - TreatmentAndAppointment "No" path
 * - scheduleReminder/FollowUp
 * - FailedToReminder
 *
 * Props:
 *   control         — react-hook-form control
 *   setValue         — react-hook-form setValue
 *   activeTime      — current selected preset key
 *   onPresetChange  — called when user selects a preset
 *   assignToState   — selected assignees array
 *   setAssignTo     — setter for assignees
 *   showStepBadges  — whether to show numbered step badges (default: true)
 *   startStep       — starting step number (default: 1)
 *   notesName       — form field name for notes (default: 'OtherNotes')
 *   notesValue      — current notes value for done/recommended badge
 */
const ReminderPresetSelector = ({
    control,
    setValue,
    clearErrors: clearErrorsProp,
    activeTime,
    onPresetChange,
    assignToState = [],
    setAssignTo,
    showStepBadges = true,
    startStep = 1,
}) => {
    const { reminderMethod } = useSelector((state) => state.schema);
    const [isOpen, setIsOpen] = useState(false);

    const handleButtonClick = (value) => {
        const btnValues = (reminder, reminderDateTime) => {
            setValue('reminder', reminder);
            setValue('reminderDateTime', reminderDateTime);
            if (clearErrorsProp) {
                clearErrorsProp('reminder');
                clearErrorsProp('reminderDate');
                clearErrorsProp('reminderDateTime');
            }
        };
        btnHandler({ value, setValue, btnValues, reminderMethod: reminderMethod || [{ value: 'CALL', label: 'Call' }] });
        onPresetChange(value);
    };

    const handleToggleOpen = () => setIsOpen(!isOpen);

    const isActive = activeTime !== 'no-reminder' && activeTime !== '';

    const CHIP = (selected, isMuted) => ({
        ...compactChipStyle(selected),
        ...(selected && isMuted && { borderColor: '#637381', color: '#fff', background: '#637381', boxShadow: '0 2px 8px rgba(99,115,129,0.24)' }),
    });

    const METHOD_CHIP = (selected) => compactChipStyle(selected);

    const renderHeader = (icon, label, step) => {
        if (!showStepBadges) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
                    <Iconify icon={icon} width={16} sx={{ color: SECTION_ICON_COLOR }} />
                    <Typography sx={SECTION_HEADER_LABEL}>{label}</Typography>
                </Box>
            );
        }
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
                <Box sx={STEP_BADGE}>{step}</Box>
                <Iconify icon={icon} width={16} sx={{ color: SECTION_ICON_COLOR }} />
                <Typography sx={SECTION_HEADER_LABEL}>{label}</Typography>
            </Box>
        );
    };

    return (
        <Box>
            {/* Reminder presets */}
            <Box sx={{ mb: 2.5 }}>
                {renderHeader('eva:bell-fill', 'Follow up reminder', startStep)}
                <Box sx={SECTION_CARD}>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: isActive ? 1.5 : 0 }}>
                        {REMINDER_PRESETS.map((preset) => (
                            <Box key={preset.key} onClick={() => handleButtonClick(preset.key)}
                                role="button" tabIndex={0} aria-pressed={activeTime === preset.key}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleButtonClick(preset.key); } }}
                                sx={CHIP(activeTime === preset.key, preset.key === 'no-reminder')}>
                                {preset.key === 'no-reminder' && <Iconify icon="mdi:bell-off-outline" width={14} sx={{ mr: 0.5 }} />}
                                {preset.label}
                            </Box>
                        ))}
                    </Box>

                    {/* Custom date picker */}
                    {activeTime === 'custom reminder' && (
                        <Box sx={{ maxWidth: 320, mb: 1.5 }}>
                            <Controller
                                name='reminderDateTime'
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <DateTimePicker
                                        open={isOpen}
                                        onOpen={handleToggleOpen}
                                        onClose={() => setIsOpen(false)}
                                        label='Date & time'
                                        // Coerce to a real Date — the date-fns adapter throws
                                        // ("toDate" on a string) if a preset stored a string here.
                                        value={field.value ? new Date(field.value) : null}
                                        format='dd/MM/yyyy HH:mm'
                                        ampm={false}
                                        minDateTime={new Date()}
                                        onChange={(newValue) => field.onChange(newValue)}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true, size: 'small',
                                                error: !!error, helperText: error?.message,
                                                onKeyDown: (e) => e.preventDefault(),
                                                sx: { '& .MuiOutlinedInput-root': { borderRadius: '10px' } }
                                            },
                                            toolbarButton: { onClick: handleToggleOpen }
                                        }}
                                    />
                                )}
                            />
                        </Box>
                    )}

                    {/* Method chips (replaces old dropdown) */}
                    {isActive && (
                        <Controller
                            name='reminder'
                            control={control}
                            render={({ field }) => (
                                <Box sx={{ mb: 1.5 }}>
                                    <Typography sx={{ fontSize: '10px', color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 600, mb: 0.75 }}>
                                        Method
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                        {(reminderMethod || []).map((opt) => (
                                            <Box key={opt.value}
                                                onClick={() => field.onChange(opt.value)}
                                                role="button" tabIndex={0} aria-pressed={field.value === opt.value}
                                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); field.onChange(opt.value); } }}
                                                sx={METHOD_CHIP(field.value === opt.value)}>
                                                {opt.label}
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        />
                    )}

                    {/* Assignees */}
                    {isActive && setAssignTo && (
                        <AssignedToLead label="Assignees" assignToState={assignToState} setAssignTo={setAssignTo} />
                    )}
                </Box>
            </Box>
        </Box>
    );
};

ReminderPresetSelector.propTypes = {
    control: PropTypes.object.isRequired,
    setValue: PropTypes.func.isRequired,
    clearErrors: PropTypes.func,
    activeTime: PropTypes.string.isRequired,
    onPresetChange: PropTypes.func.isRequired,
    assignToState: PropTypes.array,
    setAssignTo: PropTypes.func,
    showStepBadges: PropTypes.bool,
    startStep: PropTypes.number,
};

export default ReminderPresetSelector;
